/**
 * Routing Report Page
 *
 * Shows which pharmacies are fulfilling orders in each state,
 * with a state × pharmacy matrix and distribution percentages.
 */

import { useState, useEffect, useCallback } from 'react'
import { orderFlowsAPI } from '../services/api'
import {
  Card, CardContent, CardHeader, CardTitle,
  Button
} from '../components/ui'
import {
  RefreshCw, Loader2, AlertCircle, MapPin, Building2, Package
} from 'lucide-react'

const INTERVAL_OPTIONS = [
  { value: '7', label: '7 Days' },
  { value: '30', label: '30 Days' },
  { value: '90', label: '90 Days' },
  { value: '180', label: '6 Months' },
  { value: '365', label: '1 Year' },
]

function formatNum(v) {
  if (v == null) return '-'
  return new Intl.NumberFormat('en-US').format(v)
}

function pctColor(pct) {
  if (pct >= 80) return 'bg-blue-600 text-white'
  if (pct >= 50) return 'bg-blue-500 text-white'
  if (pct >= 25) return 'bg-blue-400 text-white'
  if (pct > 0) return 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300'
  return ''
}

export default function RoutingPage() {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [days, setDays] = useState('30')
  const [excludeLocal, setExcludeLocal] = useState(true)

  const fetchData = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const res = await orderFlowsAPI.getRouting({ days, excludeLocal: String(excludeLocal) })
      if (res.data.success) setData(res.data.data)
    } catch (err) {
      console.error('[ROUTING] Error:', err)
      setError(err.response?.data?.error?.message || err.response?.data?.message || err.message)
    } finally {
      setLoading(false)
    }
  }, [days, excludeLocal])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const summary = data?.summary
  const pharmacies = data?.pharmacies || []
  const pharmacyTotals = data?.pharmacy_totals || {}
  const states = data?.states || []

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground">Order Routing</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Pharmacy fulfillment distribution by patient state
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex gap-1">
            {INTERVAL_OPTIONS.map(opt => (
              <Button
                key={opt.value}
                variant={days === opt.value ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setDays(opt.value)}
                className="h-8 text-xs"
              >
                {opt.label}
              </Button>
            ))}
          </div>
          <label className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer select-none">
            <input
              type="checkbox"
              checked={excludeLocal}
              onChange={(e) => setExcludeLocal(e.target.checked)}
              className="rounded border-muted-foreground/40"
            />
            Exclude local pharmacy
          </label>
          <Button onClick={fetchData} variant="outline" size="sm" disabled={loading}>
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {error && (
        <Card className="border-destructive">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-destructive">
              <AlertCircle className="w-5 h-5" />
              <span>{error}</span>
            </div>
          </CardContent>
        </Card>
      )}

      {loading && !data && (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      )}

      {data && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-3 gap-4">
            <Card>
              <CardContent className="pt-4 pb-3">
                <div className="flex items-center gap-2 text-muted-foreground text-xs font-medium mb-1">
                  <Package className="w-3.5 h-3.5" /> Total Orders
                </div>
                <div className="text-xl font-bold">{formatNum(summary?.total_orders)}</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4 pb-3">
                <div className="flex items-center gap-2 text-muted-foreground text-xs font-medium mb-1">
                  <MapPin className="w-3.5 h-3.5" /> States
                </div>
                <div className="text-xl font-bold">{formatNum(summary?.unique_states)}</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4 pb-3">
                <div className="flex items-center gap-2 text-muted-foreground text-xs font-medium mb-1">
                  <Building2 className="w-3.5 h-3.5" /> Pharmacies
                </div>
                <div className="text-xl font-bold">{formatNum(summary?.unique_pharmacies)}</div>
              </CardContent>
            </Card>
          </div>

          {/* Pharmacy totals bar */}
          {pharmacies.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Pharmacy Volume</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {pharmacies.map(p => {
                    const total = pharmacyTotals[p] || 0
                    const pct = summary?.total_orders > 0 ? (total / summary.total_orders) * 100 : 0
                    return (
                      <div key={p} className="flex items-center gap-3">
                        <div className="w-40 text-sm font-medium truncate" title={p}>{p}</div>
                        <div className="flex-1 h-5 bg-muted rounded-full overflow-hidden">
                          <div
                            className="h-full bg-blue-500 rounded-full transition-all"
                            style={{ width: `${Math.max(pct, pct > 0 ? 1 : 0)}%` }}
                          />
                        </div>
                        <div className="w-20 text-right text-sm font-mono">{formatNum(total)}</div>
                        <div className="w-14 text-right text-xs text-muted-foreground">{pct.toFixed(1)}%</div>
                      </div>
                    )
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {/* State × Pharmacy Matrix */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">
                State × Pharmacy Matrix
                {loading && <Loader2 className="inline w-4 h-4 ml-2 animate-spin" />}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {states.length === 0 ? (
                <p className="text-sm text-muted-foreground py-8 text-center">No routing data found</p>
              ) : (
                <div className="border rounded-lg overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="text-left p-3 font-medium sticky left-0 bg-muted/50 z-10 min-w-[70px]">State</th>
                        <th className="text-right p-3 font-medium min-w-[70px]">Total</th>
                        {pharmacies.map(p => (
                          <th key={p} className="text-center p-3 font-medium min-w-[100px] whitespace-nowrap">
                            <div className="text-xs">{p}</div>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {states.map(st => (
                        <tr key={st.state} className="border-t hover:bg-muted/30">
                          <td className="p-3 font-bold sticky left-0 bg-background z-10">{st.state}</td>
                          <td className="p-3 text-right font-mono font-medium">{formatNum(st.total)}</td>
                          {st.pharmacies.map(p => (
                            <td key={p.name} className="p-2 text-center">
                              {p.count > 0 ? (
                                <div className={`inline-block px-2 py-1 rounded text-xs font-medium ${pctColor(p.pct)}`}>
                                  {p.count}
                                  <span className="text-[10px] ml-1 opacity-75">{p.pct}%</span>
                                </div>
                              ) : (
                                <span className="text-muted-foreground/30">-</span>
                              )}
                            </td>
                          ))}
                        </tr>
                      ))}
                      {/* Totals row */}
                      <tr className="border-t-2 border-foreground/20 bg-muted/30 font-bold">
                        <td className="p-3 sticky left-0 bg-muted/30 z-10">TOTAL</td>
                        <td className="p-3 text-right font-mono">{formatNum(summary?.total_orders)}</td>
                        {pharmacies.map(p => (
                          <td key={p} className="p-3 text-center font-mono text-sm">
                            {formatNum(pharmacyTotals[p])}
                          </td>
                        ))}
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  )
}
